import React, { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Wrapper from './Wrapper';
import MessageHeading from './MessageHeading';
import AdminSwitch from './AdminSwitch';
import MessageLabel from './MessageLabel';
import AdminCustomInput from './AdminCustomInput';
import AdminCustomArea from './AdminCustomArea';
import AdminImageUploadZone from './AdminImageUploadZone';
import AdminCustomRadio from './AdminCustomRadio';
import MessageSubHeading from './MessageSubHeading';

const PushNotificationSettings = ({
    formData,
    setFormData,
    handleInput,
    handleFileChange,
    selectedFile,
    uploadedImage,
    onDelete,
    combinedRef,
    formDataError,
    siteTheme,
    id,
    onlyPermission,
    sourceTitle,
    sourceDescription,
    sourceType,
    sourceTypeName,
    slug,
    prefix = "video"
}) => {
    
    // Auto-population logic
    useEffect(() => {
        if (formData?.push_notification_enabled == 1) {
            setFormData(prev => {
                let updates = {};
                if (!prev?.push_notification_title && sourceTitle) {
                    updates.push_notification_title = sourceTitle;
                }
                if (!prev?.push_notification_description && sourceDescription) {
                    // Strip HTML tags if sourceDescription is HTML (from Jodit)
                    const plainDescription = typeof sourceDescription === 'string' ? sourceDescription.replace(/<[^>]*>?/gm, '') : "";
                    updates.push_notification_description = plainDescription;
                }
                
                // Add source metadata for internal tracking/verification
                if (sourceType) updates.push_notification_source_type = sourceType;
                if (sourceTypeName) updates.push_notification_source_type_name = sourceTypeName;
                if (slug) updates.push_notification_slug = slug;

                if (Object.keys(updates).length > 0) {
                    return { ...prev, ...updates };
                }
                return prev;
            });
        }
    }, [formData?.push_notification_enabled, sourceTitle, sourceDescription, sourceType, sourceTypeName, slug]);

    if (!onlyPermission?.includes(44)) {
        return null;
    }

    return (
        <Wrapper>
            <div className="d-flex justify-content-between align-items-center">
                <MessageHeading text="Push Notification Setting" noMargin />
                <AdminSwitch id={`${prefix}-push-notification-enabled`} name="push_notification_enabled" checked={formData?.push_notification_enabled == 1} onChange={handleInput} />
            </div>
            {formData?.push_notification_enabled == 1 && (
                <div className="mt-3">
                    <MessageLabel text="Push Notification Title" />
                    <AdminCustomInput id={`${prefix}-push-notification-title`} name="push_notification_title" value={formData?.push_notification_title} onChange={handleInput} placeHolder="Enter notification title" />

                    <MessageLabel text="Push Notification Description" className="mt-3" />
                    <AdminCustomArea id={`${prefix}-push-notification-description`} name="push_notification_description" value={formData?.push_notification_description} onChange={handleInput} placeHolder="Enter notification description" rows={3} />

                    <MessageLabel text="Notification Image" className="mt-3" />
                    <MessageSubHeading text="Recommended size: 1024x512" />
                    <AdminImageUploadZone
                        idAttribute={`${prefix}-notification-image-${id || 'new'}`}
                        onChange={handleFileChange}
                        selectedFile={selectedFile}
                        uploadedImage={uploadedImage}
                        onDelete={onDelete}
                        ref={(el) => (combinedRef.current.notification_image = el)}
                        name="notification_image"
                        apiFieldName="push_notification_image"
                        error={formDataError?.notification_image}
                        data-check-size="true"
                        enableCropping={siteTheme?.enable_image_cropping === 1}
                        id={id}
                    />

                    <MessageLabel text="Publish Type" className="mt-3" />
                    <div className='d-flex align-items-center justify-content-start gap-3 mt-2'>
                        <div className="d-flex align-items-center gap-2">
                            <AdminCustomRadio id={`${prefix}-push-publish-now`} name="push_notification_publish_now" checked={formData?.push_notification_publish_type == "publish_now"} onChange={() => setFormData(prev => ({ ...prev, push_notification_publish_type: "publish_now" }))} />
                            <MessageLabel text="Publish Now" noMargin />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <AdminCustomRadio id={`${prefix}-push-publish-later`} name="push_notification_publish_later" checked={formData?.push_notification_publish_type == "publish_later"} onChange={() => setFormData(prev => ({ ...prev, push_notification_publish_type: "publish_later" }))} />
                            <MessageLabel text="Publish Later" noMargin />
                        </div>
                    </div>

                    {formData?.push_notification_publish_type == "publish_later" && (
                        <div className="mt-3">
                            <MessageLabel text="Publish Time" />
                            {/* react-datepicker: works on all browsers including Safari, which does not
                                support <input type="datetime-local"> and renders it as a plain text box */}
                            <DatePicker
                                id={`${prefix}-push-notification-publish-time`}
                                selected={formData?.push_notification_publish_time ? new Date(formData.push_notification_publish_time) : null}
                                onChange={(date) => {
                                    // Store as LOCAL time string (no UTC offset). The cron compares
                                    // scheduled time against local "now" via APP_TIMEZONE — both sides
                                    // must be the same local wall-clock format or the comparison is wrong.
                                    // toISOString() would give UTC, making IST schedules fire 5.5h early.
                                    const localStr = date ? [
                                        date.getFullYear(),
                                        String(date.getMonth() + 1).padStart(2, '0'),
                                        String(date.getDate()).padStart(2, '0')
                                    ].join('-') + 'T' + [
                                        String(date.getHours()).padStart(2, '0'),
                                        String(date.getMinutes()).padStart(2, '0'),
                                        '00'
                                    ].join(':') : "";
                                    setFormData(prev => ({ ...prev, push_notification_publish_time: localStr }));
                                }}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="yyyy-MM-dd HH:mm"
                                minDate={new Date()}
                                placeholderText="Select date & time"
                                className="custom-input custom-input-border w-100 custom-placeholder bg-transparent theme-text-color mb-3"
                                wrapperClassName="w-100"
                                autoComplete="off"
                            />
                        </div>
                    )}
                </div>
            )}
        </Wrapper>
    );
};

export default PushNotificationSettings;
